import React, { useState, useEffect, useCallback } from 'react';
import { cmsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { 
  Plus, Pencil, Trash2, Loader2, Star, FileText, HelpCircle, 
  DollarSign, Eye, EyeOff, GripVertical, RefreshCw 
} from 'lucide-react';

// ===================== TESTIMONIALS MANAGER =====================
const TestimonialsManager = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_name: '',
    role_type: 'parent',
    subject: '',
    rating: 5,
    testimonial_text: '',
    featured: false,
    published: true,
    sort_order: 0
  });

  const loadTestimonials = useCallback(async () => {
    try {
      const res = await cmsAPI.listTestimonials();
      setTestimonials(res.data.testimonials || []);
    } catch (err) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTestimonials(); }, [loadTestimonials]);

  const resetForm = () => {
    setForm({
      customer_name: '',
      role_type: 'parent',
      subject: '',
      rating: 5,
      testimonial_text: '',
      featured: false,
      published: true,
      sort_order: testimonials.length
    });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        customer_name: item.customer_name || '',
        role_type: item.role_type || 'parent',
        subject: item.subject || '',
        rating: item.rating || 5,
        testimonial_text: item.testimonial_text || '',
        featured: item.featured || false,
        published: item.published !== false,
        sort_order: item.sort_order || 0
      });
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.customer_name || !form.testimonial_text) {
      toast.error('Name and testimonial text are required');
      return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        await cmsAPI.updateTestimonial(editingItem.testimonial_id, form);
        toast.success('Testimonial updated');
      } else {
        await cmsAPI.createTestimonial(form);
        toast.success('Testimonial created');
      }
      setShowDialog(false);
      loadTestimonials();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await cmsAPI.deleteTestimonial(deleteTarget.testimonial_id);
      toast.success('Testimonial deleted');
      setDeleteTarget(null);
      loadTestimonials();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const togglePublished = async (item) => {
    try {
      await cmsAPI.updateTestimonial(item.testimonial_id, { published: !item.published });
      loadTestimonials();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const toggleFeatured = async (item) => {
    try {
      await cmsAPI.updateTestimonial(item.testimonial_id, { featured: !item.featured });
      loadTestimonials();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Testimonials</h3>
          <p className="text-sm text-slate-500">Manage customer reviews displayed on the homepage</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadTestimonials}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-1" /> Add Testimonial
          </Button>
        </div>
      </div>

      {testimonials.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Star className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No testimonials yet. Add your first customer review!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {testimonials.map((item) => (
            <Card key={item.testimonial_id} className={`${!item.published ? 'opacity-60 bg-slate-50' : ''}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium">{item.customer_name}</span>
                      <Badge variant="outline" className="text-xs">{item.role_type}</Badge>
                      {item.subject && <Badge variant="secondary" className="text-xs">{item.subject}</Badge>}
                      {item.featured && <Badge className="bg-amber-500 text-xs">Featured</Badge>}
                      {!item.published && <Badge variant="destructive" className="text-xs">Draft</Badge>}
                    </div>
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`h-4 w-4 ${i < item.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                      ))}
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">{item.testimonial_text}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => togglePublished(item)}
                      title={item.published ? 'Unpublish' : 'Publish'}
                    >
                      {item.published ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => toggleFeatured(item)}
                      title={item.featured ? 'Unfeature' : 'Feature'}
                    >
                      <Star className={`h-4 w-4 ${item.featured ? 'fill-amber-400 text-amber-400' : 'text-slate-400'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(item)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Testimonial' : 'Add Testimonial'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer Name *</Label>
                <Input 
                  value={form.customer_name} 
                  onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))}
                  placeholder="Sarah M."
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role_type} onValueChange={(v) => setForm(f => ({ ...f, role_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="guardian">Guardian</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Subject (optional)</Label>
                <Input 
                  value={form.subject} 
                  onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
                  placeholder="Math & Physics"
                />
              </div>
              <div className="space-y-2">
                <Label>Rating</Label>
                <Select value={String(form.rating)} onValueChange={(v) => setForm(f => ({ ...f, rating: parseInt(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[5, 4, 3, 2, 1].map(r => (
                      <SelectItem key={r} value={String(r)}>{r} Star{r > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Testimonial Text *</Label>
              <Textarea 
                value={form.testimonial_text} 
                onChange={(e) => setForm(f => ({ ...f, testimonial_text: e.target.value }))}
                placeholder="Share the customer's experience..."
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input 
                  type="number"
                  value={form.sort_order} 
                  onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.published} onCheckedChange={(v) => setForm(f => ({ ...f, published: v }))} />
                <Label>Published</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm(f => ({ ...f, featured: v }))} />
                <Label>Featured</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingItem ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Testimonial?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the testimonial from "{deleteTarget?.customer_name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ===================== FAQ MANAGER =====================
const FAQManager = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    question: '',
    answer: '',
    category: '',
    published: true,
    sort_order: 0
  });

  const loadFaqs = useCallback(async () => {
    try {
      const res = await cmsAPI.listFaqs();
      setFaqs(res.data.faqs || []);
    } catch (err) {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFaqs(); }, [loadFaqs]);

  const resetForm = () => {
    setForm({
      question: '',
      answer: '',
      category: '',
      published: true,
      sort_order: faqs.length
    });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        question: item.question || '',
        answer: item.answer || '',
        category: item.category || '',
        published: item.published !== false,
        sort_order: item.sort_order || 0
      });
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.question || !form.answer) {
      toast.error('Question and answer are required');
      return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        await cmsAPI.updateFaq(editingItem.faq_id, form);
        toast.success('FAQ updated');
      } else {
        await cmsAPI.createFaq(form);
        toast.success('FAQ created');
      }
      setShowDialog(false);
      loadFaqs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await cmsAPI.deleteFaq(deleteTarget.faq_id);
      toast.success('FAQ deleted');
      setDeleteTarget(null);
      loadFaqs();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const togglePublished = async (item) => {
    try {
      await cmsAPI.updateFaq(item.faq_id, { published: !item.published });
      loadFaqs();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">FAQ</h3>
          <p className="text-sm text-slate-500">Manage frequently asked questions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadFaqs}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-1" /> Add FAQ
          </Button>
        </div>
      </div>

      {faqs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <HelpCircle className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No FAQs yet. Add common questions and answers!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {faqs.map((item) => (
            <Card key={item.faq_id} className={`${!item.published ? 'opacity-60 bg-slate-50' : ''}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{item.question}</span>
                      {item.category && <Badge variant="outline" className="text-xs">{item.category}</Badge>}
                      {!item.published && <Badge variant="destructive" className="text-xs">Draft</Badge>}
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">{item.answer}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => togglePublished(item)} title={item.published ? 'Unpublish' : 'Publish'}>
                      {item.published ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(item)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Question *</Label>
              <Input 
                value={form.question} 
                onChange={(e) => setForm(f => ({ ...f, question: e.target.value }))}
                placeholder="How do I book a session?"
              />
            </div>
            <div className="space-y-2">
              <Label>Answer *</Label>
              <Textarea 
                value={form.answer} 
                onChange={(e) => setForm(f => ({ ...f, answer: e.target.value }))}
                placeholder="Provide a clear answer..."
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category (optional)</Label>
                <Input 
                  value={form.category} 
                  onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                  placeholder="General, Pricing, Sessions..."
                />
              </div>
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input 
                  type="number"
                  value={form.sort_order} 
                  onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.published} onCheckedChange={(v) => setForm(f => ({ ...f, published: v }))} />
              <Label>Published</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingItem ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete FAQ?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this FAQ. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ===================== POLICIES MANAGER =====================
const PoliciesManager = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    content: '',
    policy_type: 'general',
    published: true,
    sort_order: 0
  });

  const POLICY_TYPES = [
    { value: 'payment', label: 'Payment' },
    { value: 'cancellation', label: 'Cancellation' },
    { value: 'tutoring', label: 'Tutoring' },
    { value: 'attendance', label: 'Attendance' },
    { value: 'online_sessions', label: 'Online Sessions' },
    { value: 'packages', label: 'Packages' },
    { value: 'general', label: 'General' }
  ];

  const loadPolicies = useCallback(async () => {
    try {
      const res = await cmsAPI.listPolicies();
      setPolicies(res.data.policies || []);
    } catch (err) {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPolicies(); }, [loadPolicies]);

  const resetForm = () => {
    setForm({
      title: '',
      content: '',
      policy_type: 'general',
      published: true,
      sort_order: policies.length
    });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        title: item.title || '',
        content: item.content || '',
        policy_type: item.policy_type || 'general',
        published: item.published !== false,
        sort_order: item.sort_order || 0
      });
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.content || !form.policy_type) {
      toast.error('Title, content, and type are required');
      return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        await cmsAPI.updatePolicy(editingItem.policy_id, form);
        toast.success('Policy updated');
      } else {
        await cmsAPI.createPolicy(form);
        toast.success('Policy created');
      }
      setShowDialog(false);
      loadPolicies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await cmsAPI.deletePolicy(deleteTarget.policy_id);
      toast.success('Policy deleted');
      setDeleteTarget(null);
      loadPolicies();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const togglePublished = async (item) => {
    try {
      await cmsAPI.updatePolicy(item.policy_id, { published: !item.published });
      loadPolicies();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Policies</h3>
          <p className="text-sm text-slate-500">Manage policy pages displayed on the website</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadPolicies}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-1" /> Add Policy
          </Button>
        </div>
      </div>

      {policies.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No policies yet. Add your terms, privacy, and other policies!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {policies.map((item) => (
            <Card key={item.policy_id} className={`${!item.published ? 'opacity-60 bg-slate-50' : ''}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{item.title}</span>
                      <Badge variant="outline" className="text-xs capitalize">{item.policy_type?.replace('_', ' ')}</Badge>
                      {!item.published && <Badge variant="destructive" className="text-xs">Draft</Badge>}
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">{item.content}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => togglePublished(item)} title={item.published ? 'Unpublish' : 'Publish'}>
                      {item.published ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(item)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Policy' : 'Add Policy'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input 
                  value={form.title} 
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Privacy Policy"
                />
              </div>
              <div className="space-y-2">
                <Label>Policy Type *</Label>
                <Select value={form.policy_type} onValueChange={(v) => setForm(f => ({ ...f, policy_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {POLICY_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Content *</Label>
              <Textarea 
                value={form.content} 
                onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Enter the full policy content..."
                rows={12}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input 
                  type="number"
                  value={form.sort_order} 
                  onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.published} onCheckedChange={(v) => setForm(f => ({ ...f, published: v }))} />
              <Label>Published</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingItem ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Policy?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteTarget?.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ===================== PRICING PACKAGES MANAGER =====================
const PricingPackagesManager = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    package_name: '',
    hours: 5,
    total_price: 275,
    hourly_rate: 55,
    short_description: '',
    badge_text: '',
    featured: false,
    published: true,
    sort_order: 0
  });

  const loadPackages = useCallback(async () => {
    try {
      const res = await cmsAPI.listPricingPackages();
      setPackages(res.data.packages || []);
    } catch (err) {
      toast.error('Failed to load pricing packages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPackages(); }, [loadPackages]);

  const resetForm = () => {
    setForm({
      package_name: '',
      hours: 5,
      total_price: 275,
      hourly_rate: 55,
      short_description: '',
      badge_text: '',
      featured: false,
      published: true,
      sort_order: packages.length
    });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        package_name: item.package_name || '',
        hours: item.hours || 5,
        total_price: item.total_price || 275,
        hourly_rate: item.hourly_rate || 55,
        short_description: item.short_description || '',
        badge_text: item.badge_text || '',
        featured: item.featured || false,
        published: item.published !== false,
        sort_order: item.sort_order || 0
      });
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.package_name || !form.hours || !form.hourly_rate) {
      toast.error('Package name, hours, and hourly rate are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        total_price: form.hours * form.hourly_rate
      };
      if (editingItem) {
        await cmsAPI.updatePricingPackage(editingItem.package_id, payload);
        toast.success('Pricing package updated');
      } else {
        await cmsAPI.createPricingPackage(payload);
        toast.success('Pricing package created');
      }
      setShowDialog(false);
      loadPackages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await cmsAPI.deletePricingPackage(deleteTarget.package_id);
      toast.success('Pricing package deleted');
      setDeleteTarget(null);
      loadPackages();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const togglePublished = async (item) => {
    try {
      await cmsAPI.updatePricingPackage(item.package_id, { published: !item.published });
      loadPackages();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const toggleFeatured = async (item) => {
    try {
      await cmsAPI.updatePricingPackage(item.package_id, { featured: !item.featured });
      loadPackages();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Public Pricing Packages</h3>
          <p className="text-sm text-slate-500">Pre-defined packages shown on the pricing page</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadPackages}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-1" /> Add Package
          </Button>
        </div>
      </div>

      {packages.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <DollarSign className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No pricing packages yet. Create pre-defined bundles for customers!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.map((item) => (
            <Card key={item.package_id} className={`relative ${!item.published ? 'opacity-60 bg-slate-50' : ''} ${item.featured ? 'ring-2 ring-amber-400' : ''}`}>
              {item.badge_text && (
                <div className="absolute -top-2 -right-2">
                  <Badge className="bg-amber-500">{item.badge_text}</Badge>
                </div>
              )}
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{item.package_name}</CardTitle>
                  {!item.published && <Badge variant="destructive" className="text-xs">Draft</Badge>}
                </div>
                {item.short_description && (
                  <CardDescription>{item.short_description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold">${item.total_price?.toFixed(0)}</span>
                    <span className="text-slate-500">/ {item.hours} hrs</span>
                  </div>
                  <p className="text-sm text-slate-500">${item.hourly_rate?.toFixed(2)}/hour</p>
                </div>
                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => togglePublished(item)} title={item.published ? 'Unpublish' : 'Publish'}>
                      {item.published ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleFeatured(item)} title={item.featured ? 'Unfeature' : 'Feature'}>
                      <Star className={`h-4 w-4 ${item.featured ? 'fill-amber-400 text-amber-400' : 'text-slate-400'}`} />
                    </Button>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(item)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Pricing Package' : 'Add Pricing Package'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Package Name *</Label>
                <Input 
                  value={form.package_name} 
                  onChange={(e) => setForm(f => ({ ...f, package_name: e.target.value }))}
                  placeholder="Starter"
                />
              </div>
              <div className="space-y-2">
                <Label>Badge Text</Label>
                <Input 
                  value={form.badge_text} 
                  onChange={(e) => setForm(f => ({ ...f, badge_text: e.target.value }))}
                  placeholder="Most Popular"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Short Description</Label>
              <Input 
                value={form.short_description} 
                onChange={(e) => setForm(f => ({ ...f, short_description: e.target.value }))}
                placeholder="Perfect for trying our services"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Hours *</Label>
                <Input 
                  type="number"
                  value={form.hours} 
                  onChange={(e) => {
                    const hours = parseFloat(e.target.value) || 0;
                    setForm(f => ({ ...f, hours, total_price: hours * f.hourly_rate }));
                  }}
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label>Hourly Rate *</Label>
                <Input 
                  type="number"
                  value={form.hourly_rate} 
                  onChange={(e) => {
                    const rate = parseFloat(e.target.value) || 0;
                    setForm(f => ({ ...f, hourly_rate: rate, total_price: f.hours * rate }));
                  }}
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Total Price</Label>
                <Input 
                  type="number"
                  value={form.total_price?.toFixed(2)} 
                  disabled
                  className="bg-slate-50"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Display Order</Label>
              <Input 
                type="number"
                value={form.sort_order} 
                onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.published} onCheckedChange={(v) => setForm(f => ({ ...f, published: v }))} />
                <Label>Published</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm(f => ({ ...f, featured: v }))} />
                <Label>Featured</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingItem ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pricing Package?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteTarget?.package_name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ===================== PRICING TIERS MANAGER =====================
const PricingTiersManager = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    level_key: 'highSchool',
    level_label: 'High School',
    min_hours: 1,
    max_hours: 5,
    hourly_rate: 45,
    discount_percentage: 0,
    badge_text: '',
    published: true,
    sort_order: 0
  });

  const loadTiers = useCallback(async () => {
    try {
      const res = await cmsAPI.listPricingTiers();
      setTiers(res.data.tiers || []);
    } catch (err) {
      toast.error('Failed to load pricing tiers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTiers(); }, [loadTiers]);

  const resetForm = () => {
    setForm({
      level_key: 'highSchool',
      level_label: 'High School',
      min_hours: 1,
      max_hours: 5,
      hourly_rate: 45,
      discount_percentage: 0,
      badge_text: '',
      published: true,
      sort_order: tiers.length
    });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        level_key: item.level_key || 'highSchool',
        level_label: item.level_label || 'High School',
        min_hours: item.min_hours || 1,
        max_hours: item.max_hours || 5,
        hourly_rate: item.hourly_rate || 45,
        discount_percentage: item.discount_percentage || 0,
        badge_text: item.badge_text || '',
        published: item.published !== false,
        sort_order: item.sort_order || 0
      });
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.level_key || !form.hourly_rate) {
      toast.error('Level and hourly rate are required');
      return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        await cmsAPI.updatePricingTier(editingItem.tier_id, form);
        toast.success('Pricing tier updated');
      } else {
        await cmsAPI.createPricingTier(form);
        toast.success('Pricing tier created');
      }
      setShowDialog(false);
      loadTiers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await cmsAPI.deletePricingTier(deleteTarget.tier_id);
      toast.success('Pricing tier deleted');
      setDeleteTarget(null);
      loadTiers();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const togglePublished = async (item) => {
    try {
      await cmsAPI.updatePricingTier(item.tier_id, { published: !item.published });
      loadTiers();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  // Group tiers by level
  const groupedTiers = tiers.reduce((acc, tier) => {
    const key = tier.level_key || 'other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(tier);
    return acc;
  }, {});

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Pricing Calculator Tiers</h3>
          <p className="text-sm text-slate-500">Configure hourly rates and discount tiers for the pricing calculator</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadTiers}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-1" /> Add Tier
          </Button>
        </div>
      </div>

      {tiers.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <DollarSign className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-2">No pricing tiers configured yet.</p>
            <p className="text-sm text-slate-400">Default pricing will be used until you add custom tiers.</p>
            <p className="text-xs text-slate-400 mt-4">
              Default: High School ($45/$40/$35), University ($50/$45/$40)
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedTiers).map(([levelKey, levelTiers]) => (
            <Card key={levelKey}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg capitalize">
                  {levelTiers[0]?.level_label || levelKey.replace(/([A-Z])/g, ' $1').trim()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {levelTiers.sort((a, b) => a.sort_order - b.sort_order).map((item) => (
                    <div 
                      key={item.tier_id} 
                      className={`p-4 rounded-xl border-2 ${!item.published ? 'opacity-60 bg-slate-50 border-slate-200' : 'border-stone-200'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-600">
                          {item.min_hours}-{item.max_hours >= 999 ? '∞' : item.max_hours} hours
                        </span>
                        {item.discount_percentage > 0 && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            {item.discount_percentage}% OFF
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-baseline gap-1 mb-3">
                        <span className="text-2xl font-bold text-slate-900">${item.hourly_rate}</span>
                        <span className="text-slate-500">/hour</span>
                      </div>
                      {!item.published && <Badge variant="destructive" className="text-xs mb-2">Draft</Badge>}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <Button variant="ghost" size="sm" onClick={() => togglePublished(item)}>
                          {item.published ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
                        </Button>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(item)} className="text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Pricing Tier' : 'Add Pricing Tier'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Student Level *</Label>
                <Select value={form.level_key} onValueChange={(v) => {
                  const label = v === 'highSchool' ? 'High School' : 'University';
                  setForm(f => ({ ...f, level_key: v, level_label: label }));
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="highSchool">High School</SelectItem>
                    <SelectItem value="university">University</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Badge Text</Label>
                <Input 
                  value={form.badge_text} 
                  onChange={(e) => setForm(f => ({ ...f, badge_text: e.target.value }))}
                  placeholder="Best Value"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Hours *</Label>
                <Input 
                  type="number"
                  value={form.min_hours} 
                  onChange={(e) => setForm(f => ({ ...f, min_hours: parseInt(e.target.value) || 1 }))}
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Hours * (999 = unlimited)</Label>
                <Input 
                  type="number"
                  value={form.max_hours} 
                  onChange={(e) => setForm(f => ({ ...f, max_hours: parseInt(e.target.value) || 999 }))}
                  min="1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hourly Rate ($) *</Label>
                <Input 
                  type="number"
                  value={form.hourly_rate} 
                  onChange={(e) => setForm(f => ({ ...f, hourly_rate: parseFloat(e.target.value) || 0 }))}
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Discount % (display only)</Label>
                <Input 
                  type="number"
                  value={form.discount_percentage} 
                  onChange={(e) => setForm(f => ({ ...f, discount_percentage: parseInt(e.target.value) || 0 }))}
                  min="0"
                  max="100"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Display Order</Label>
              <Input 
                type="number"
                value={form.sort_order} 
                onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.published} onCheckedChange={(v) => setForm(f => ({ ...f, published: v }))} />
              <Label>Published</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingItem ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pricing Tier?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this pricing tier. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ===================== MAIN CMS CONTENT MANAGER =====================
const CMSContentManager = () => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Website Content Management</h2>
          <p className="text-sm text-slate-500">Manage public website content without code changes</p>
        </div>
      </div>

      <Tabs defaultValue="testimonials" className="w-full">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="testimonials" data-testid="cms-tab-testimonials">
            <Star className="h-4 w-4 mr-1" /> Testimonials
          </TabsTrigger>
          <TabsTrigger value="faqs" data-testid="cms-tab-faqs">
            <HelpCircle className="h-4 w-4 mr-1" /> FAQs
          </TabsTrigger>
          <TabsTrigger value="policies" data-testid="cms-tab-policies">
            <FileText className="h-4 w-4 mr-1" /> Policies
          </TabsTrigger>
          <TabsTrigger value="pricing-tiers" data-testid="cms-tab-pricing-tiers">
            <DollarSign className="h-4 w-4 mr-1" /> Pricing Tiers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="testimonials">
          <TestimonialsManager />
        </TabsContent>
        <TabsContent value="faqs">
          <FAQManager />
        </TabsContent>
        <TabsContent value="policies">
          <PoliciesManager />
        </TabsContent>
        <TabsContent value="pricing-tiers">
          <PricingTiersManager />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CMSContentManager;
